import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import type { CommentPage, ReactionSummary, ReactorPage, SpaceComment, UserList } from "@repo/shared-types";
import { api } from "@/lib/api";
import { requireSession } from "@/lib/guards";
import { errorMessage } from "@/lib/session";

/**
 * A space's conversation, reached by fetchers from the pages that show a
 * thread — an event's, a result's, and the log's read of the thread on your
 * own result. No page of its own: what a thread needs beyond its first page,
 * loaded where it is shown, and every write to it, after which the page reads
 * the thread again.
 *
 * Reads, by ?want=:
 *   comments   the next page of a thread: ?event=, ?entry= for a result's,
 *              ?team= for a team's
 *   replies    the rest of a comment's replies: ?comment=
 *   reactors   who reacted: ?event=, ?entry= or ?team=, ?comment=, ?name=
 *   members    who typing @ suggests: ?q=
 * each with ?cursor=. Under /t/me, with ?entry= or ?team=, the first two read
 * the thread on your own result, or your team's, from the log.
 */

export type ConversationData = CommentPage | ReactorPage | UserList;

export async function conversationLoader({ request, params }: LoaderFunctionArgs): Promise<ConversationData> {
  await requireSession(request);
  const slug = params.slug ?? "";
  const query = new URL(request.url).searchParams;
  const get = (name: string) => query.get(name) ?? "";
  const cursor = query.get("cursor");
  const space = api.space(slug);
  const fromLog = slug === "me" && get("entry") !== "";
  const fromTeamInLog = slug === "me" && get("team") !== "";
  const thread = () =>
    get("entry")
      ? space.events.results.conversation(get("event"), get("entry"))
      : get("team")
        ? space.events.teams.conversation(get("event"), get("team"))
        : space.events.conversation(get("event"));

  switch (get("want")) {
    case "comments":
      if (fromLog) return api.entries.comments(get("entry"), cursor);
      if (fromTeamInLog) return api.entries.teams.comments(get("team"), cursor);
      return thread().comments(cursor);
    case "replies":
      if (fromLog) return api.entries.replies(get("entry"), get("comment"), cursor);
      if (fromTeamInLog) return api.entries.teams.replies(get("team"), get("comment"), cursor);
      return space.comments.replies(get("comment"), cursor);
    case "reactors": {
      const page = { name: query.get("name"), cursor };
      if (get("comment")) return space.comments.reactors(get("comment"), page);
      return thread().reactors(page);
    }
    case "members":
      return space.members.search(get("q"));
    default:
      throw new Response("Not Found", { status: 404 });
  }
}

export type ConversationResult = {
  error?: string;
  /** A comment written or edited, as the api now has it. */
  comment?: SpaceComment;
  /** A comment deleted. */
  deleted?: string;
  /** How people reacted, after the caller's reaction changed. */
  reactions?: ReactionSummary;
};

export async function conversationAction({ request, params }: ActionFunctionArgs): Promise<ConversationResult> {
  const form = await request.formData();
  const space = api.space(params.slug ?? "");
  const get = (name: string) => String(form.get(name) ?? "");
  const thread = () =>
    get("entry")
      ? space.events.results.conversation(get("event"), get("entry"))
      : get("team")
        ? space.events.teams.conversation(get("event"), get("team"))
        : space.events.conversation(get("event"));

  try {
    switch (get("intent")) {
      case "comment": {
        const text = get("text").trim();
        if (!text) return { error: "Write something first." };
        return {
          comment: await thread().comment({
            text,
            ...(get("id") ? { id: get("id") } : {}),
            ...(get("replyTo") ? { replyTo: get("replyTo") } : {}),
          }),
        };
      }
      case "edit": {
        const text = get("text").trim();
        if (!text) return { error: "A comment says something: delete it instead." };
        return { comment: await space.comments.edit(get("comment"), { text }) };
      }
      case "delete":
        await space.comments.delete(get("comment"));
        return { deleted: get("comment") };
      case "react":
        return {
          reactions: get("comment")
            ? await space.comments.react(get("comment"), get("name"))
            : await thread().react(get("name")),
        };
      case "unreact":
        return {
          reactions: get("comment") ? await space.comments.unreact(get("comment")) : await thread().unreact(),
        };
      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}
